import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token;

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email: string, password: string, deviceId: string) =>
    api.post('/api/auth/login', { email, password, deviceId }),

  register: (data: { email: string; password: string; name: string }) =>
    api.post('/api/auth/register', data),

  me: () => api.get('/api/auth/me'),

  logout: () => api.post('/api/auth/logout'),

  refresh: (refreshToken: string) =>
    api.post('/api/auth/refresh', { refreshToken }),
};

export const usersApi = {
  list: () => api.get('/api/users'),
  get: (id: string) => api.get(`/api/users/${id}`),
  create: (data: { email: string; name: string; password: string; role?: string }) =>
    api.post('/api/users', data),
  update: (id: string, data: any) => api.put(`/api/users/${id}`, data),
  delete: (id: string) => api.delete(`/api/users/${id}`),
  revokeSessions: (id: string) => api.post(`/api/users/${id}/revoke-sessions`),
};

export const categoriesApi = {
  list: () => api.get('/api/categories'),
  get: (id: string) => api.get(`/api/categories/${id}`),
  create: (data: { name: string; description?: string; icon?: string; color?: string }) =>
    api.post('/api/categories', data),
  update: (id: string, data: any) => api.put(`/api/categories/${id}`, data),
  delete: (id: string) => api.delete(`/api/categories/${id}`),
};

export const documentsApi = {
  list: (params?: { categoryId?: string; search?: string }) =>
    api.get('/api/documents', { params }),
  get: (id: string) => api.get(`/api/documents/${id}`),
  upload: (formData: FormData) =>
    api.post('/api/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  update: (id: string, data: any) => api.put(`/api/documents/${id}`, data),
  delete: (id: string) => api.delete(`/api/documents/${id}`),
  process: (id: string) => api.post(`/api/documents/${id}/process`),
  getViewUrl: (id: string) => `${API_BASE_URL}/api/documents/${id}/view`,
  getDownloadUrl: (id: string) => `${API_BASE_URL}/api/documents/${id}/download`,
};

export const chatApi = {
  listSessions: () => api.get('/api/chat/sessions'),
  getSession: (id: string) => api.get(`/api/chat/sessions/${id}`),
  createSession: (title?: string) => api.post('/api/chat/sessions', { title }),
  deleteSession: (id: string) => api.delete(`/api/chat/sessions/${id}`),
  sendMessage: (message: string, sessionId?: string) =>
    api.post('/api/chat/message', { message, sessionId }),
};

export const searchApi = {
  search: (q: string, categoryId?: string) =>
    api.get('/api/search', { params: { q, categoryId } }),
  semantic: (query: string, categoryId?: string) =>
    api.post('/api/search/semantic', { query, categoryId }),
};

// ===============================
// SISTEMA DE COTIZACIONES
// ===============================

export const suppliersApi = {
  list: (params?: { search?: string; category?: string; isActive?: string }) =>
    api.get('/api/suppliers', { params }),
  get: (id: string) => api.get(`/api/suppliers/${id}`),
  getCategories: () => api.get('/api/suppliers/categories'),
  create: (data: {
    name: string;
    email: string;
    phone?: string;
    contactName?: string;
    address?: string;
    category?: string;
    notes?: string;
  }) => api.post('/api/suppliers', data),
  update: (id: string, data: any) => api.put(`/api/suppliers/${id}`, data),
  delete: (id: string) => api.delete(`/api/suppliers/${id}`),
};

export const quoteRequestsApi = {
  list: (params?: { status?: string; search?: string }) =>
    api.get('/api/quote-requests', { params }),
  get: (id: string) => api.get(`/api/quote-requests/${id}`),
  create: (data: {
    title: string;
    description?: string;
    projectName?: string;
    requiredByDate?: string;
    items?: Array<{
      description: string;
      specifications?: string;
      quantity: number;
      unit: string;
      requiredBrand?: string;
      notes?: string;
    }>;
    supplierIds?: string[];
  }) => api.post('/api/quote-requests', data),
  update: (id: string, data: any) => api.put(`/api/quote-requests/${id}`, data),
  delete: (id: string) => api.delete(`/api/quote-requests/${id}`),

  // Items
  addItem: (id: string, item: {
    description: string;
    specifications?: string;
    quantity: number;
    unit: string;
    requiredBrand?: string;
    notes?: string;
  }) => api.post(`/api/quote-requests/${id}/items`, item),
  updateItem: (id: string, itemId: string, data: any) =>
    api.put(`/api/quote-requests/${id}/items/${itemId}`, data),
  deleteItem: (id: string, itemId: string) =>
    api.delete(`/api/quote-requests/${id}/items/${itemId}`),

  // Suppliers
  addSuppliers: (id: string, supplierIds: string[]) =>
    api.post(`/api/quote-requests/${id}/suppliers`, { supplierIds }),
  removeSupplier: (id: string, supplierId: string) =>
    api.delete(`/api/quote-requests/${id}/suppliers/${supplierId}`),

  // Actions
  send: (id: string) => api.post(`/api/quote-requests/${id}/send`),
  close: (id: string) => api.post(`/api/quote-requests/${id}/close`),
  cancel: (id: string) => api.post(`/api/quote-requests/${id}/cancel`),
};

export const quotesApi = {
  list: (params?: { quoteRequestId?: string; supplierId?: string }) =>
    api.get('/api/quotes', { params }),
  get: (id: string) => api.get(`/api/quotes/${id}`),
  getByQuoteRequest: (quoteRequestId: string) =>
    api.get(`/api/quotes/by-request/${quoteRequestId}`),
  getComparison: (quoteRequestId: string) =>
    api.get(`/api/quotes/comparison/${quoteRequestId}`),
  create: (data: {
    quoteRequestId: string;
    supplierId: string;
    quoteNumber?: string;
    validUntil?: string;
    paymentTerms?: string;
    deliveryTime?: string;
    currency?: string;
    subtotal?: number;
    taxes?: number;
    total?: number;
    items?: Array<{
      quoteRequestItemId?: string;
      description: string;
      brand?: string;
      model?: string;
      partNumber?: string;
      specifications?: string;
      quantity: number;
      unit: string;
      unitPrice: number;
      totalPrice: number;
      deliveryTime?: string;
      warranty?: string;
      notes?: string;
    }>;
  }) => api.post('/api/quotes', data),
  update: (id: string, data: any) => api.put(`/api/quotes/${id}`, data),
  updateItem: (id: string, itemId: string, data: any) =>
    api.put(`/api/quotes/${id}/items/${itemId}`, data),

  // Attachments
  listAttachments: (quoteId: string) =>
    api.get(`/api/quotes/${quoteId}/attachments`),
  uploadAttachment: (quoteId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/quotes/${quoteId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAttachment: (quoteId: string, attachmentId: string) =>
    api.get(`/api/quotes/${quoteId}/attachments/${attachmentId}`),
  downloadAttachment: (quoteId: string, attachmentId: string) =>
    api.get(`/api/quotes/${quoteId}/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    }),
  deleteAttachment: (quoteId: string, attachmentId: string) =>
    api.delete(`/api/quotes/${quoteId}/attachments/${attachmentId}`),

  // AI Extraction
  extractFromAttachment: (quoteId: string, attachmentId: string) =>
    api.post(`/api/quotes/${quoteId}/attachments/${attachmentId}/extract`),
  createItemsFromExtraction: (quoteId: string, data: { items: any[]; matchedItems?: any[] }) =>
    api.post(`/api/quotes/${quoteId}/items/from-extraction`, data),
};

// ===============================
// CLIENTES (CRM)
// ===============================

export const clientsApi = {
  list: (params?: { search?: string; type?: string }) =>
    api.get('/api/clients', { params }),
  get: (id: string) => api.get(`/api/clients/${id}`),
  getIndustries: () => api.get('/api/clients/industries'),
  create: (data: any) => api.post('/api/clients', data),
  update: (id: string, data: any) => api.put(`/api/clients/${id}`, data),
  delete: (id: string) => api.delete(`/api/clients/${id}`),
  addContact: (id: string, data: any) => api.post(`/api/clients/${id}/contacts`, data),
  removeContact: (id: string, contactId: string) => api.delete(`/api/clients/${id}/contacts/${contactId}`),
};

// ===============================
// PROYECTOS
// ===============================

export const projectsApi = {
  list: (params?: { search?: string; status?: string; clientId?: string }) =>
    api.get('/api/projects', { params }),
  get: (id: string) => api.get(`/api/projects/${id}`),
  create: (data: any) => api.post('/api/projects', data),
  update: (id: string, data: any) => api.put(`/api/projects/${id}`, data),
  delete: (id: string) => api.delete(`/api/projects/${id}`),

  // Members
  addMember: (id: string, data: { userId: string; role?: string }) =>
    api.post(`/api/projects/${id}/members`, data),
  removeMember: (id: string, userId: string) =>
    api.delete(`/api/projects/${id}/members/${userId}`),

  // Phases
  addPhase: (id: string, data: any) => api.post(`/api/projects/${id}/phases`, data),
  updatePhase: (id: string, phaseId: string, data: any) =>
    api.put(`/api/projects/${id}/phases/${phaseId}`, data),
  deletePhase: (id: string, phaseId: string) =>
    api.delete(`/api/projects/${id}/phases/${phaseId}`),

  // Tasks
  addTask: (id: string, data: any) => api.post(`/api/projects/${id}/tasks`, data),
  updateTask: (id: string, taskId: string, data: any) =>
    api.put(`/api/projects/${id}/tasks/${taskId}`, data),
  deleteTask: (id: string, taskId: string) =>
    api.delete(`/api/projects/${id}/tasks/${taskId}`),
};

// ===============================
// INVENTARIO
// ===============================

export const inventoryApi = {
  // Categories
  listCategories: () => api.get('/api/inventory/categories'),
  createCategory: (data: any) => api.post('/api/inventory/categories', data),
  updateCategory: (id: string, data: any) => api.put(`/api/inventory/categories/${id}`, data),
  deleteCategory: (id: string) => api.delete(`/api/inventory/categories/${id}`),

  // Items
  listItems: (params?: { search?: string; categoryId?: string; lowStock?: string }) =>
    api.get('/api/inventory/items', { params }),
  getItem: (id: string) => api.get(`/api/inventory/items/${id}`),
  createItem: (data: any) => api.post('/api/inventory/items', data),
  updateItem: (id: string, data: any) => api.put(`/api/inventory/items/${id}`, data),
  deleteItem: (id: string) => api.delete(`/api/inventory/items/${id}`),

  // Movements
  listMovements: (params?: { itemId?: string; type?: string; projectId?: string }) =>
    api.get('/api/inventory/movements', { params }),
  addMovement: (data: any) => api.post('/api/inventory/movements', data),
};

// ===============================
// PRESUPUESTOS
// ===============================

export const budgetsApi = {
  list: (params?: { search?: string; status?: string; clientId?: string; projectId?: string }) =>
    api.get('/api/budgets', { params }),
  get: (id: string) => api.get(`/api/budgets/${id}`),
  create: (data: any) => api.post('/api/budgets', data),
  update: (id: string, data: any) => api.put(`/api/budgets/${id}`, data),
  delete: (id: string) => api.delete(`/api/budgets/${id}`),
  duplicate: (id: string) => api.post(`/api/budgets/${id}/duplicate`),

  // Status
  send: (id: string) => api.post(`/api/budgets/${id}/send`),
  approve: (id: string) => api.post(`/api/budgets/${id}/approve`),
  reject: (id: string) => api.post(`/api/budgets/${id}/reject`),

  // Chapters
  addChapter: (id: string, data: any) => api.post(`/api/budgets/${id}/chapters`, data),
  updateChapter: (id: string, chapterId: string, data: any) =>
    api.put(`/api/budgets/${id}/chapters/${chapterId}`, data),
  deleteChapter: (id: string, chapterId: string) =>
    api.delete(`/api/budgets/${id}/chapters/${chapterId}`),

  // Items
  addItem: (id: string, chapterId: string, data: any) =>
    api.post(`/api/budgets/${id}/chapters/${chapterId}/items`, data),
  updateItem: (id: string, chapterId: string, itemId: string, data: any) =>
    api.put(`/api/budgets/${id}/chapters/${chapterId}/items/${itemId}`, data),
  deleteItem: (id: string, chapterId: string, itemId: string) =>
    api.delete(`/api/budgets/${id}/chapters/${chapterId}/items/${itemId}`),
};

// ===============================
// FACTURACIÓN
// ===============================

export const invoicesApi = {
  list: (params?: { search?: string; type?: string; status?: string; clientId?: string }) =>
    api.get('/api/invoices', { params }),
  get: (id: string) => api.get(`/api/invoices/${id}`),
  getSummary: () => api.get('/api/invoices/summary'),
  create: (data: any) => api.post('/api/invoices', data),
  update: (id: string, data: any) => api.put(`/api/invoices/${id}`, data),
  delete: (id: string) => api.delete(`/api/invoices/${id}`),

  // Status
  send: (id: string) => api.post(`/api/invoices/${id}/send`),
  voidInvoice: (id: string) => api.post(`/api/invoices/${id}/void`),

  // Items
  addItem: (id: string, data: any) => api.post(`/api/invoices/${id}/items`, data),
  updateItem: (id: string, itemId: string, data: any) =>
    api.put(`/api/invoices/${id}/items/${itemId}`, data),
  deleteItem: (id: string, itemId: string) =>
    api.delete(`/api/invoices/${id}/items/${itemId}`),

  // Payments
  addPayment: (id: string, data: any) => api.post(`/api/invoices/${id}/payments`, data),
};

// ===============================
// CENTROS DE COSTO
// ===============================

export const costCentersApi = {
  list: (params?: { projectId?: string; includeInactive?: string }) =>
    api.get('/api/cost-centers', { params }),
  get: (id: string) => api.get(`/api/cost-centers/${id}`),
  create: (data: {
    projectId?: string;
    code?: string;
    name: string;
    description?: string;
    budget?: number;
    color?: string;
    order?: number;
  }) => api.post('/api/cost-centers', data),
  update: (id: string, data: any) => api.put(`/api/cost-centers/${id}`, data),
  delete: (id: string) => api.delete(`/api/cost-centers/${id}`),
};

// ===============================
// BANDEJA DE FACTURAS (ingesta con IA)
// ===============================

export const invoiceInboxApi = {
  list: (params?: { reviewStatus?: string; supplierId?: string; projectId?: string; search?: string; page?: number; limit?: number }) =>
    api.get('/api/invoice-inbox', { params }),
  get: (id: string) => api.get(`/api/invoice-inbox/${id}`),
  upload: (file: File, projectId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (projectId) formData.append('projectId', projectId);
    return api.post('/api/invoice-inbox/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  fileBlob: (id: string) =>
    api.get(`/api/invoice-inbox/${id}/file`, { responseType: 'blob' }),
  routeItem: (id: string, itemId: string, data: { costCenterId?: string | null; costType?: string | null }) =>
    api.patch(`/api/invoice-inbox/${id}/items/${itemId}/route`, data),
  routeBulk: (id: string, assignments: Array<{ itemId: string; costCenterId?: string | null; costType?: string | null }>) =>
    api.post(`/api/invoice-inbox/${id}/route-bulk`, { assignments }),
  confirm: (id: string) => api.post(`/api/invoice-inbox/${id}/confirm`),
  reject: (id: string) => api.post(`/api/invoice-inbox/${id}/reject`),
  setSupplier: (id: string, supplierId: string) =>
    api.patch(`/api/invoice-inbox/${id}/supplier`, { supplierId }),
};

// ===============================
// FINANZAS DE PROYECTO (totalización e indicadores)
// ===============================

export const projectFinanceApi = {
  getSpend: (id: string) => api.get(`/api/projects/${id}/spend`),
  updateSettings: (id: string, data: { contractValue?: number; estimatedBudget?: number }) =>
    api.patch(`/api/projects/${id}/finance-settings`, data),
  listAdvances: (id: string) => api.get(`/api/projects/${id}/advances`),
  addAdvance: (id: string, data: { amount: number; date?: string; concept?: string; reference?: string }) =>
    api.post(`/api/projects/${id}/advances`, data),
  deleteAdvance: (id: string, advanceId: string) =>
    api.delete(`/api/projects/${id}/advances/${advanceId}`),
};

// ===============================
// RECURSOS HUMANOS
// ===============================

export const hrApi = {
  // Departments
  listDepartments: () => api.get('/api/hr/departments'),
  createDepartment: (data: any) => api.post('/api/hr/departments', data),
  updateDepartment: (id: string, data: any) => api.put(`/api/hr/departments/${id}`, data),
  deleteDepartment: (id: string) => api.delete(`/api/hr/departments/${id}`),

  // Employees
  listEmployees: (params?: { search?: string; departmentId?: string; status?: string }) =>
    api.get('/api/hr/employees', { params }),
  getEmployee: (id: string) => api.get(`/api/hr/employees/${id}`),
  createEmployee: (data: any) => api.post('/api/hr/employees', data),
  updateEmployee: (id: string, data: any) => api.put(`/api/hr/employees/${id}`, data),
  terminateEmployee: (id: string) => api.post(`/api/hr/employees/${id}/terminate`),

  // Attendance
  listAttendance: (params?: { employeeId?: string; startDate?: string; endDate?: string }) =>
    api.get('/api/hr/attendance', { params }),
  addAttendance: (data: any) => api.post('/api/hr/attendance', data),
  checkIn: () => api.post('/api/hr/attendance/check-in'),
  checkOut: () => api.post('/api/hr/attendance/check-out'),
};

// ===============================
// CALENDARIO
// ===============================

export const calendarApi = {
  list: (params?: { startDate?: string; endDate?: string; type?: string }) =>
    api.get('/api/calendar', { params }),
  get: (id: string) => api.get(`/api/calendar/${id}`),
  getUpcoming: (limit?: number) => api.get('/api/calendar/upcoming', { params: { limit } }),
  create: (data: any) => api.post('/api/calendar', data),
  update: (id: string, data: any) => api.put(`/api/calendar/${id}`, data),
  delete: (id: string) => api.delete(`/api/calendar/${id}`),
};

// ===============================
// ORDENES DE COMPRA
// ===============================

export const purchaseOrdersApi = {
  list: (params?: { search?: string; status?: string; supplierId?: string; projectId?: string }) =>
    api.get('/api/purchase-orders', { params }),
  get: (id: string) => api.get(`/api/purchase-orders/${id}`),
  create: (data: any) => api.post('/api/purchase-orders', data),
  update: (id: string, data: any) => api.put(`/api/purchase-orders/${id}`, data),
  delete: (id: string) => api.delete(`/api/purchase-orders/${id}`),

  // Items
  addItem: (id: string, data: any) => api.post(`/api/purchase-orders/${id}/items`, data),
  updateItem: (id: string, itemId: string, data: any) =>
    api.put(`/api/purchase-orders/${id}/items/${itemId}`, data),
  removeItem: (id: string, itemId: string) =>
    api.delete(`/api/purchase-orders/${id}/items/${itemId}`),

  // Status
  approve: (id: string) => api.post(`/api/purchase-orders/${id}/approve`),
  send: (id: string) => api.post(`/api/purchase-orders/${id}/send`),
  cancel: (id: string) => api.post(`/api/purchase-orders/${id}/cancel`),

  // Receipts
  listReceipts: (id: string) => api.get(`/api/purchase-orders/${id}/receipts`),
  createReceipt: (id: string, data: any) => api.post(`/api/purchase-orders/${id}/receipts`, data),
};

// ===============================
// ERROR HANDLING
// ===============================

export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string; error?: string }>;

    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error;
    }

    if (axiosError.message) {
      return axiosError.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Ocurrió un error inesperado';
};
